import type { ElementConfig } from '../components/ui-element.type';
import { mutateTo } from './mutations/index';
import { createElement } from './render-api';
import type { UIIcon } from '../components/icon/ui-icon';

interface LoadingState {
    loading: boolean;
}

/**
 * @typedef {object} ButtonLoaderOptions
 * @property {boolean} [fast] Animation speed
 * @property {boolean} [disabled] Disabled state
 * @property {boolean} [loading] Loading state
 */
interface ButtonLoaderOptions {
    fast?: boolean;
    disabled?: boolean;
    loading?: boolean;
}

/**
 * @memberof SharedComponents
 * @class Loader
 * @alias loader
 * @classdesc
 * Helper methods for adding progress loaders.
 */

/**
 * Toggle loader icon for button
 * @memberof Loader
 * @param {HTMLButtonElement} button
 * @param {boolean} [active]
 * @param {ButtonLoaderOptions} [options]
 * @returns {ElementConfig}
 */
const buildButtonLoaderConfig = (
    button: HTMLButtonElement,
    active: boolean = false,
    options: ButtonLoaderOptions = {}
): ElementConfig => {
    const existingIcon = button.querySelector<UIIcon>(
        'ui-icon[glyph="loader"]'
    );
    if (!active && existingIcon) {
        existingIcon.remove();
    }

    const existingChildren = [...button.childNodes].filter(
        (child) =>
            !(
                child instanceof Element &&
                child.matches('ui-icon[glyph="loader"]')
            )
    );

    return {
        element: button,
        attributes: {
            disabled: options.disabled,
        },
        classList: {
            '-loading': active,
        },
        children: [
            ...existingChildren,
            active
                ? createElement({
                      tagName: 'ui-icon',
                      element: existingIcon,
                      attributes: {
                          glyph: 'loader',
                          color: 'white',
                          fast: !!options.fast,
                      },
                  })
                : null,
        ].filter(Boolean),
    };
};

/**
 * Adds loader icon for button.
 * @memberof Loader
 * @param {HTMLButtonElement} button
 * @param {ButtonLoaderOptions} [options]
 * @returns {object} Object with loading property that controls the loader state
 */
const addButtonLoader = (
    button: HTMLButtonElement,
    options: ButtonLoaderOptions = {}
): LoadingState => {
    let isLoading = options.loading ?? false;
    let loaderIcon: UIIcon | null = null;
    button.disabled = !!options.disabled;

    const updateLoader = (loading: boolean): void => {
        if (loading) {
            button.classList.add('-loading');
            if (!loaderIcon) {
                loaderIcon = createElement({
                    tagName: 'ui-icon',
                    attributes: {
                        glyph: 'loader',
                        color: 'white',
                        fast: !!options.fast,
                    },
                })!;
                button.append(loaderIcon);
            }
        } else {
            button.classList.remove('-loading');
            loaderIcon?.remove();
        }
    };

    updateLoader(isLoading);
    const loaderState: LoadingState = {
        loading: false,
    };

    // For backward compatibility, also add the loading property directly
    // This ensures the object behaves like the original ReactiveElementState
    // Can use Reflect.defineProperty(), in case of exceptions with some proxies.
    // Currently, we do not use proxies, so Object.defineProperty is fine.
    Object.defineProperty(loaderState, 'loading', {
        get() {
            return isLoading;
        },
        set(value: boolean) {
            if (isLoading !== value) {
                isLoading = value;
                updateLoader(isLoading);
            }
        },
        enumerable: true,
        configurable: true,
    });

    return loaderState;
};

/**
 * Toggle loader icon for button
 * @memberof Loader
 * @param {HTMLButtonElement} button
 * @param {boolean} [active]
 * @param {ButtonLoaderOptions} [options]
 */
const toggleLoaderInButton = (
    button: HTMLButtonElement,
    active: boolean = false,
    options: ButtonLoaderOptions = {}
): void => {
    mutateTo(
        button,
        createElement(buildButtonLoaderConfig(button, active, options))!
    );
};

export { addButtonLoader, toggleLoaderInButton, buildButtonLoaderConfig };
